import { Slider, Switch } from "@eweadn-qiankun/shared-components";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { resolveHfdMagneticTravelRangeMm } from "@eweadn-qiankun/shared-utils";

/** 磁轴高级键默认：触发行程 / RT（与 performance DEFAULT_RT 对齐） */
export const HFD_ADV_DEFAULT_TRIGGER_STROKE = 1.5;
export const HFD_ADV_DEFAULT_PRESS_RT = 0.3;
export const HFD_ADV_DEFAULT_RELEASE_RT = 0.3;
/** SOCD：默认开启 RT（与此前始终写入 RT 行为一致） */
export const HFD_ADV_DEFAULT_RT_ENABLED = true;
export const HFD_ADV_TRAVEL_MIN = 0.1;
export const HFD_ADV_TRAVEL_MAX = 3.5;
export const HFD_ADV_RT_MIN = 0.01;
export const HFD_ADV_RT_MAX = 3.4;
export const HFD_ADV_TRAVEL_STEP = 0.01;

type Props = {
  checkData: any;
  getAfterChangeData: (data: any) => void;
  /** SOCD：可开关 RT，并显示按下/抬起；RS 仅触发行程 */
  showRt?: boolean;
  keyboardType?: string;
};

/**
 * 华奋达磁轴：迅洁 / SOCD 共用的快速触发行程（及可选 RT）调节。
 * 值写在 checkData.otherData，保存时由 Home 写入 setPerformance。
 * 注意：SOCD 的 otherData.mode 是行为模式，RT 开关用 rtEnabled。
 */
const MagneticTravelRtPanel = ({
  checkData,
  getAfterChangeData,
  showRt = false,
  keyboardType,
}: Props) => {
  const { t } = useTranslation();
  const { min: travelMin, max: travelMax } = useMemo(
    () => resolveHfdMagneticTravelRangeMm(keyboardType),
    [keyboardType]
  );
  const rtMax = Math.min(HFD_ADV_RT_MAX, travelMax);
  const other = checkData?.otherData ?? {};

  const patchOther = (patch: Record<string, number | boolean>) => {
    const next = JSON.parse(JSON.stringify(checkData));
    next.otherData = { ...next.otherData, ...patch };
    getAfterChangeData(next);
  };

  const triggerStroke = Number(
    other.triggerKeyStroke ?? HFD_ADV_DEFAULT_TRIGGER_STROKE
  );
  const rtEnabled =
    other.rtEnabled === undefined
      ? HFD_ADV_DEFAULT_RT_ENABLED
      : Boolean(other.rtEnabled);
  const pressRT = Number(other.pressRT ?? HFD_ADV_DEFAULT_PRESS_RT);
  const releaseRT = Number(other.releaseRT ?? HFD_ADV_DEFAULT_RELEASE_RT);

  return (
    <div className="advance-setting-box magnetic-travel-rt-panel">
      <div className="advance-setting-box-header">
        <div className="advance-setting-box-header-title">
          {t("keyboard.advancedKey.magneticTravel.title")}
        </div>
        <div className="advance-setting-box-header-desc">
          {t("keyboard.advancedKey.magneticTravel.desc")}
        </div>
      </div>
      <div className="operation-item operation-mt">
        <div className="mt-mode-box">
          <div className="common-slider">
            <div className="slider-item">
              <div className="label">{t("keyboard.trigger.triggerStroke")}</div>
              <div className="value">
                <Slider
                  value={triggerStroke}
                  onChange={(v) => patchOther({ triggerKeyStroke: Number(v) })}
                  onChangeComplete={(v) =>
                    patchOther({ triggerKeyStroke: Number(v) })
                  }
                  min={travelMin}
                  max={travelMax}
                  step={HFD_ADV_TRAVEL_STEP}
                  unit="mm"
                  showValue={true}
                />
              </div>
            </div>
            {showRt ? (
              <>
                <div className="slider-item magnetic-rt-switch-row">
                  <div className="label">
                    {t("keyboard.trigger.rapidTrigger")}
                  </div>
                  <Switch
                    checked={rtEnabled}
                    onChange={(checked) =>
                      patchOther({ rtEnabled: Boolean(checked) })
                    }
                  />
                </div>
                {rtEnabled ? (
                  <>
                    <div className="slider-item">
                      <div className="label magnetic-travel-rt-label">
                        {t("keyboard.advancedKey.magneticTravel.press")}
                      </div>
                      <div className="value">
                        <Slider
                          value={pressRT}
                          onChange={(v) => patchOther({ pressRT: Number(v) })}
                          onChangeComplete={(v) =>
                            patchOther({ pressRT: Number(v) })
                          }
                          min={HFD_ADV_RT_MIN}
                          max={rtMax}
                          step={HFD_ADV_TRAVEL_STEP}
                          unit="mm"
                          showValue={true}
                        />
                      </div>
                    </div>
                    <div className="slider-item">
                      <div className="label magnetic-travel-rt-label">
                        {t("keyboard.advancedKey.magneticTravel.release")}
                      </div>
                      <div className="value">
                        <Slider
                          value={releaseRT}
                          onChange={(v) => patchOther({ releaseRT: Number(v) })}
                          onChangeComplete={(v) =>
                            patchOther({ releaseRT: Number(v) })
                          }
                          min={HFD_ADV_RT_MIN}
                          max={rtMax}
                          step={HFD_ADV_TRAVEL_STEP}
                          unit="mm"
                          showValue={true}
                        />
                      </div>
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MagneticTravelRtPanel;
